/**
 * Indicadores del encabezado de Torre de Control.
 *
 * El diseño no usa el mismo juego de indicadores en todas las pantallas: el
 * kanban Por Estatus muestra seis (con el numeral en grande) y la vista de
 * lista muestra cinco (con el porcentaje en grande). Las vistas Por Usuario y
 * Por Ubicación en kanban no llevan fila de indicadores: cada columna trae sus
 * propios chips.
 *
 * Todo eso vive aquí en vez de repartido por los componentes para que cambiarlo
 * sea una edición y no una cacería.
 */

export const INDICATOR_DEFINITIONS = {
  total: { key: 'total', label: 'Total de Tareas', metric: 'total', showPercent: false },
  pending: { key: 'pending', label: 'Pendientes', metric: 'in_progress' },
  overdue: { key: 'overdue', label: 'Vencidas', metric: 'overdue' },
  in_progress: { key: 'in_progress', label: 'En proceso', metric: 'in_progress' },
  finished: { key: 'finished', label: 'Finalizadas', metric: 'finished' },
  suspended: { key: 'suspended', label: 'Suspendidas', metric: 'suspended' },
  escalated: { key: 'escalated', label: 'Escaladas', metric: 'escalated' }
}

/**
 * `emphasis` decide qué número va en grande: en el kanban manda la cantidad
 * (`4/20` grande, `20%` chico) y en la lista manda el porcentaje.
 */
export const INDICATOR_SETS = {
  'status:kanban': {
    emphasis: 'count',
    keys: ['total', 'overdue', 'in_progress', 'finished', 'suspended', 'escalated']
  },
  'status:list': {
    emphasis: 'percent',
    keys: ['total', 'pending', 'overdue', 'escalated', 'finished']
  },
  'user:kanban': { emphasis: 'count', keys: [] },
  'user:list': {
    emphasis: 'percent',
    keys: ['total', 'pending', 'overdue', 'escalated', 'finished']
  },
  'location:kanban': { emphasis: 'count', keys: [] },
  'location:list': {
    emphasis: 'percent',
    keys: ['total', 'pending', 'overdue', 'escalated', 'finished']
  }
}

export function indicatorSetFor (viewType, layout) {
  return INDICATOR_SETS[`${viewType}:${layout}`] || INDICATOR_SETS['status:kanban']
}

export function percentOf (value, total) {
  if (!total) return 0
  return Math.round((value / total) * 100)
}

/**
 * Convierte el `summary` del backend en las tarjetas de indicador que pinta la
 * fila superior.
 *
 * @param {object} summary conteos del backend
 * @param {string} viewType status | user | location
 * @param {string} layout kanban | list
 */
export function buildIndicators (summary, viewType, layout) {
  const set = indicatorSetFor(viewType, layout)
  const totals = summary || {}
  const total = Number(totals.total || 0)

  return set.keys.map(key => {
    const definition = INDICATOR_DEFINITIONS[key]
    const value = Number(totals[definition.metric] || 0)
    const showPercent = definition.showPercent !== false

    return {
      key,
      label: definition.label,
      value,
      total,
      percent: percentOf(value, total),
      showPercent,
      // El indicador Total no filtra a ninguna columna: es el "quitar filtro".
      columnKey: key === 'total' ? null : definition.metric,
      emphasis: set.emphasis
    }
  })
}

/**
 * Texto que va en grande y texto que va en chico, según la pantalla.
 */
export function indicatorFaces (indicator) {
  const fraction = indicator.showPercent ? `${indicator.value}/${indicator.total}` : `${indicator.value}`
  const percent = `${indicator.percent}%`

  if (!indicator.showPercent) {
    return { primary: fraction, secondary: '' }
  }

  return indicator.emphasis === 'percent'
    ? { primary: percent, secondary: fraction }
    : { primary: fraction, secondary: percent }
}
